import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, RefObject } from 'react';
import { useAppState, useStore } from '../app/context.js';
import { copy, timeOf } from '../copy.js';
import { Icon } from '../parts/Icon.js';
import { cls } from '../parts/classes.js';
import type { WidgetVals } from '../select/grid.js';
import { retryMedia } from '../state/edit.js';
import { claimSound, mediaStopped, releaseSound } from '../state/playback.js';
import { useReducedMotion } from '../stage/motion.js';
import { useStageScale } from '../stage/Stage.js';
import { color, icon } from '../tokens.js';

type MediaVals = NonNullable<WidgetVals['media']>;

/** Keeps a media element in step with the playback state: it plays only while its item is the one playing. */
export function useMediaSync(ref: RefObject<HTMLMediaElement | null>, key: string, playing: boolean, pos: number): void {
  const s = useStore();
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (!playing) { element.pause(); return; }
    if (Math.abs(element.currentTime - pos) > 1) { try { element.currentTime = pos; } catch { /* not seekable yet */ } }
    const started = element.play();
    if (started) started.catch(() => mediaStopped(s, key));
    // Only a change of `playing` starts or stops the element; `pos` is where it reports to, not a command.
  }, [playing]);
}

const isHls = (url: string): boolean => /\.m3u8($|[?#])/i.test(url);

/** Gives a video element its source; streams (.m3u8) go through hls.js where the browser cannot play them itself. */
function useVideoSource(ref: RefObject<HTMLVideoElement | null>, url: string, token: string, onError: () => void): void {
  useEffect(() => {
    const element = ref.current;
    if (!element || !url) return;
    if (!isHls(url) || element.canPlayType('application/vnd.apple.mpegurl')) { element.src = url; return; }
    let live = true, stop = () => undefined as void;
    void import('hls.js').then(({ default: Hls }) => {
      if (!live) return;
      if (!Hls.isSupported()) { onError(); return; }
      const hls = new Hls();
      hls.on(Hls.Events.ERROR, (_event, data) => { if (data.fatal) onError(); });
      hls.loadSource(url);
      hls.attachMedia(element);
      stop = () => hls.destroy();
    }, onError);
    return () => { live = false; stop(); };
  }, [token]);
}

/** The panel that replaces a photo or video that did not load (02 §3.23). */
function Broken({ text, onRetry }: { readonly text: string; readonly onRetry: (() => void) | null }) {
  return <div role="status" style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%', boxSizing: 'border-box', padding: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, textAlign: 'center', background: color.mediaBrokenBg }}>
    <span style={{ fontSize: 14, lineHeight: 1.35, color: color.text }}>{text}</span>
    {onRetry && <button type="button" className={cls('ctl', 'qb')} onClick={onRetry} style={{ height: 44, padding: '0 16px', border: 'none', borderRadius: 22, background: 'transparent', boxShadow: `inset 0 0 0 1px ${color.line}`, color: color.textStrong, fontFamily: 'inherit', fontSize: 14, cursor: 'pointer' }}>{copy.tryAgain}</button>}
  </div>;
}

/** How long a photo or video that did not load waits before it is loaded again by itself. After the last of these it is broken. */
const RELOAD_AFTER_MS = [600, 1800] as const;

/**
 * Loading with second chances: a photo or video that does not load is loaded again by itself, twice, before
 * the broken panel shows. A connection the server refused in a busy moment, or the site restarting, is over
 * soon, and the same address then works. `token` names what is loaded; a new address or a press on Try
 * again starts afresh. `turn` is the element's key, so each try is a new element.
 */
function useLoad(token: string): { readonly turn: string; readonly waiting: boolean; readonly failed: boolean; readonly fail: () => void } {
  const [at, setAt] = useState<{ readonly token: string; readonly tries: number; readonly phase: 'loading' | 'waiting' | 'failed' }>({ token, tries: 0, phase: 'loading' });
  const now = at.token === token ? at : { token, tries: 0, phase: 'loading' as const };
  const turn = `${token}|${now.tries}`;
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null), settled = useRef('');
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); timer.current = null; settled.current = ''; }, [token]);
  const fail = () => {
    // One element can report its failure more than once (a video: the element and its stream).
    if (settled.current === turn) return;
    settled.current = turn;
    const wait = RELOAD_AFTER_MS[now.tries];
    if (wait === undefined) { setAt({ token, tries: now.tries, phase: 'failed' }); return; }
    setAt({ token, tries: now.tries, phase: 'waiting' });
    timer.current = setTimeout(() => { timer.current = null; setAt({ token, tries: now.tries + 1, phase: 'loading' }); }, wait);
  };
  return { turn, waiting: now.phase === 'waiting', failed: now.phase === 'failed', fail };
}

const fill = { position: 'absolute', left: 0, top: 0, width: '100%', height: '100%', display: 'block', objectFit: 'cover' } as const;

/**
 * Draws the frame an image shows right now onto `board`, cropped like `object-fit: cover` of a box of
 * `width` × `height` stage pixels. The crop comes from the box's own ratio, not from the rounded size of the
 * canvas, so the held frame sits where the picture sits. `density`: canvas pixels per stage pixel.
 */
function holdFrame(image: HTMLImageElement, board: HTMLCanvasElement, width: number, height: number, density: number): void {
  const iw = image.naturalWidth, ih = image.naturalHeight;
  if (!iw || !ih || width <= 0 || height <= 0) return;
  const cover = Math.max(width / iw, height / ih), sw = width / cover, sh = height / cover;
  const fit = Math.min(density, 2048 / width, 2048 / height);
  board.width = Math.max(1, Math.round(width * fit)); board.height = Math.max(1, Math.round(height * fit));
  const pen = board.getContext('2d');
  if (!pen) return;
  pen.imageSmoothingQuality = 'high';
  pen.drawImage(image, (iw - sw) / 2, (ih - sh) / 2, sw, sh, 0, 0, board.width, board.height);
}

/**
 * A photo: the image in its framed box, or the broken panel.
 *
 * GIFs (owner, 4 October 2026) arrive as animated pictures and pause without any control or sign: a mouse over
 * one holds it while it stays there; a tap or click holds it until the next. Holding draws the frame on show
 * onto a canvas laid over the picture. A canvas copy is never quite the picture itself, so a still photo
 * gets neither the canvas nor the layer that listens: hovering or tapping it changes nothing (`m.anim`).
 */
export function Photo({ w, m, editing }: { readonly w: WidgetVals; readonly m: MediaVals; readonly editing: boolean }) {
  const s = useStore(), retry = useAppState().mediaRetry[w.key] ?? 0, scale = useStageScale();
  const load = useLoad(`${m.url}|${retry}`);
  const [held, setHeld] = useState(false), [hovered, setHovered] = useState(false);
  const image = useRef<HTMLImageElement | null>(null), board = useRef<HTMLCanvasElement | null>(null);
  const frozen = m.anim && !editing && (held || hovered);
  useLayoutEffect(() => {
    const img = image.current, canvas = board.current;
    if (frozen && img && canvas && img.complete) holdFrame(img, canvas, m.w, m.h, scale * (window.devicePixelRatio || 1));
    // Only the moment it freezes decides the frame.
  }, [frozen]);
  if (!m.url || load.failed) return <Broken text={m.brokenText} onRetry={editing ? null : () => retryMedia(s, w.key)} />;
  return <>
    <div style={{ position: 'absolute', left: m.x, top: m.y, width: m.w, height: m.h, transition: m.tr, pointerEvents: 'none' }}>
      {/* Between two tries the box stays empty: an image that failed would show the browser's own broken mark. */}
      {!load.waiting && <img key={load.turn} ref={image} alt={m.alt} src={m.url} draggable={false} onError={load.fail} style={fill} />}
      {m.anim && <canvas ref={board} aria-hidden="true" data-v1-held={frozen ? 'true' : 'false'} style={{ ...fill, width: '100%', height: '100%', visibility: frozen ? 'visible' : 'hidden' }} />}
    </div>
    {m.anim && !editing && <div data-v1-photo-hold="" aria-hidden="true"
      onPointerEnter={(e) => { if (e.pointerType === 'mouse') setHovered(true); }}
      onPointerLeave={(e) => { if (e.pointerType === 'mouse') setHovered(false); }}
      onClick={() => setHeld(!held)}
      style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%' }} />}
  </>;
}

/** How long the controls of a tapped video stay up (touch and pen; a mouse shows them while it is over the video). */
const CONTROLS_MS = 3000;
/** A control is a 44 px target (or a half of a small video) with a round dark disc of `size` in its middle. */
const target = { padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' } as const;
const disc = (size: number) => ({ flex: 'none', width: size, height: size, borderRadius: '50%', background: 'rgba(13, 16, 24, 0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center' }) as const;

/**
 * A video (owner, 4 October 2026): it plays on its own, muted and in a loop (paused at first under reduced
 * motion). Its controls show while a mouse is over it, for a few seconds after a tap, and while one of them
 * has keyboard focus: play/pause in the middle, sound at the top right, the timeline along the bottom.
 * Below 2 × 2 cells only play/pause and sound, each on a half of the video. A paused video keeps its play
 * button showing. Nothing is shown while its grid is edited. Its sound takes the app's one playback slot
 * (state/playback.ts). Keyboard focus keeps the controls up; a mouse or finger press does not.
 */
export function Video({ w, m, editing }: { readonly w: WidgetVals; readonly m: MediaVals; readonly editing: boolean }) {
  const s = useStore(), st = useAppState(), retry = st.mediaRetry[w.key] ?? 0, v = w.video, reduced = useReducedMotion();
  const load = useLoad(`${m.url}|${retry}`);
  const [duration, setDuration] = useState<number | null>(null);
  const [time, setTime] = useState(0);
  const [paused, setPaused] = useState(reduced);
  const [shown, setShown] = useState(false), [focused, setFocused] = useState(false);
  const ref = useRef<HTMLVideoElement | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null), pointer = useRef('mouse');
  const sound = !!st.play && st.play.key === w.key && !st.play.paused;
  const broken = !m.url || load.failed;
  // Its sound is kept while it is loaded again and given up only once it is broken.
  useEffect(() => { if (load.failed) releaseSound(s, w.key); }, [load.failed]);
  useVideoSource(ref, broken ? '' : m.url, load.turn, load.fail);
  const start = (element: HTMLVideoElement) => {
    // Refused (the browser allows no playing here): show it paused. Interrupted, or no source yet: `loadeddata` retries.
    element.play()?.catch((error: unknown) => { if (error instanceof Error && error.name === 'NotAllowedError') setPaused(true); });
  };
  useEffect(() => { const element = ref.current; if (element) element.muted = !sound; }, [sound, load.turn]);
  useEffect(() => {
    const element = ref.current;
    if (!element || broken) return;
    if (paused) element.pause(); else start(element);
  }, [paused, load.turn, broken]);
  useEffect(() => () => { if (hideTimer.current) clearTimeout(hideTimer.current); }, []);
  if (!v) return null;
  if (broken) return <Broken text={m.brokenText} onRetry={editing ? null : () => retryMedia(s, w.key)} />;
  const len = duration ?? v.len, all = shown || focused, playShown = all || paused;
  // A tap shows the controls for a while; every press on one of them keeps them up a while longer.
  const arm = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = pointer.current === 'mouse' ? null : setTimeout(() => setShown(false), CONTROLS_MS);
  };
  const togglePaused = () => { setPaused(!paused); arm(); };
  const toggleSound = () => { if (sound) releaseSound(s, w.key); else claimSound(s, w.key, len); arm(); };
  const seek = (to: number) => { const element = ref.current; if (element) { try { element.currentTime = to; } catch { /* not seekable yet */ } } setTime(to); arm(); };
  const fade = (on: boolean) => ({ opacity: on ? 1 : 0, pointerEvents: on ? 'auto' : 'none', transition: 'opacity 160ms ease' }) as const;
  const playButton = (box: CSSProperties, size: number, glyph: number) => <button type="button" className={cls('ctl')} data-v1-play="" aria-label={paused ? copy.widget.playVideo : copy.widget.pauseVideo} onClick={togglePaused}
    style={{ ...target, ...box, ...fade(playShown) }}>
    <span style={disc(size)}><Icon d={paused ? icon.play : icon.pause} size={glyph} fill={color.white} /></span>
  </button>;
  const soundButton = (box: CSSProperties, size: number, glyph: number) => <button type="button" className={cls('ctl')} data-v1-sound="" aria-label={sound ? copy.widget.muteVideo : copy.widget.unmuteVideo} aria-pressed={sound} onClick={toggleSound}
    style={{ ...target, ...box, ...fade(all) }}>
    <span style={disc(size)}><Icon d={sound ? icon.sound : icon.muted} size={glyph} stroke={color.white} width={1.8} /></span>
  </button>;
  return <>
    <div aria-hidden="true" style={{ position: 'absolute', left: m.x, top: m.y, width: m.w, height: m.h, transition: m.tr, pointerEvents: 'none' }}>
      <video key={load.turn} ref={ref} playsInline loop muted preload="auto" aria-label={m.alt} style={fill}
        onError={load.fail}
        onLoadedMetadata={(e) => { const d = e.currentTarget.duration; if (Number.isFinite(d) && d > 0) setDuration(d); }}
        onLoadedData={(e) => { if (!paused && e.currentTarget.paused) start(e.currentTarget); }}
        onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)} />
    </div>
    {v.controls && <div data-v1-video-controls="" data-shown={all ? 'true' : 'false'}
      onPointerDown={(e) => { pointer.current = e.pointerType; setFocused(false); }}
      onPointerEnter={(e) => { if (e.pointerType === 'mouse') setShown(true); }}
      onPointerLeave={(e) => { if (e.pointerType === 'mouse') setShown(false); }}
      onPointerUp={(e) => { if (e.pointerType === 'mouse' || e.target !== e.currentTarget) return; if (shown) setShown(false); else { setShown(true); arm(); } }}
      onFocus={(e) => { if (e.target.matches(':focus-visible')) setFocused(true); }} onBlur={(e) => { if (!(e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget))) setFocused(false); }}
      style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%' }}>
      {v.compact
        ? <>
          {playButton({ position: 'absolute', left: 0, top: 0, width: '50%', height: '100%', justifyContent: 'flex-end', paddingRight: 3 }, 30, 13)}
          {soundButton({ position: 'absolute', right: 0, top: 0, width: '50%', height: '100%', justifyContent: 'flex-start', paddingLeft: 3 }, 30, 15)}
        </>
        : <>
          {playButton({ position: 'absolute', left: '50%', top: '50%', width: 56, height: 56, margin: '-28px 0 0 -28px' }, 56, 22)}
          {soundButton({ position: 'absolute', right: 2, top: 2, width: 44, height: 44 }, 32, 17)}
          <input type="range" className={cls('seek')} data-v1-seek="" aria-label={copy.widget.videoPosition} aria-valuetext={timeOf(time, len)}
            min={0} max={Math.max(0.1, len)} step={0.1} value={Math.min(time, len)} onChange={(e) => seek(Number(e.currentTarget.value))}
            style={{ position: 'absolute', left: 12, right: 12, bottom: 6, width: 'calc(100% - 24px)', height: 20, ...fade(all), ['--seek' as string]: `${Math.round(Math.min(1, time / Math.max(0.1, len)) * 100)}%` }} />
        </>}
    </div>}
  </>;
}
